import { ErrorCode } from "@revenuecat/purchases-js";

export type RevenueCatPurchaseErrorCategory =
  | "user_cancelled"
  | "network"
  | "operation_in_progress"
  | "payment_pending"
  | "store_problem"
  | "payment_rejected"
  | "invalid_subscriber_attributes"
  | "invalid_email"
  | "configuration"
  | "product_unavailable"
  | "already_purchased"
  | "unknown";

export type NormalizedRevenueCatPurchaseError = {
  category: RevenueCatPurchaseErrorCategory;
  errorCode: number | null;
  statusCode: number | null;
  backendErrorCode: string | number | null;
  isCancelled: boolean;
  requiresVerification: boolean;
};

type RevenueCatErrorLike = {
  errorCode?: unknown;
  extra?: {
    statusCode?: unknown;
    backendErrorCode?: unknown;
  };
};

function asFiniteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function asSafeBackendCode(value: unknown): string | number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && /^[a-zA-Z0-9_.-]{1,80}$/.test(value)) return value;
  return null;
}

function categoryForErrorCode(errorCode: number | null): RevenueCatPurchaseErrorCategory {
  switch (errorCode) {
    case ErrorCode.UserCancelledError:
      return "user_cancelled";
    case ErrorCode.NetworkError:
      return "network";
    case ErrorCode.OperationAlreadyInProgressError:
      return "operation_in_progress";
    case ErrorCode.PaymentPendingError:
      return "payment_pending";
    case ErrorCode.StoreProblemError:
      return "store_problem";
    case ErrorCode.PurchaseNotAllowedError:
    case ErrorCode.PurchaseInvalidError:
      return "payment_rejected";
    case ErrorCode.InvalidEmailError:
      return "invalid_email";
    case ErrorCode.InvalidSubscriberAttributesError:
      return "invalid_subscriber_attributes";
    case ErrorCode.ConfigurationError:
    case ErrorCode.InvalidCredentialsError:
    case ErrorCode.InvalidAppUserIdError:
      return "configuration";
    case ErrorCode.ProductNotAvailableForPurchaseError:
      return "product_unavailable";
    case ErrorCode.ProductAlreadyPurchasedError:
    case ErrorCode.ReceiptAlreadyInUseError:
      return "already_purchased";
    default:
      return "unknown";
  }
}

export function normalizeRevenueCatPurchaseError(
  error: unknown
): NormalizedRevenueCatPurchaseError {
  const candidate =
    typeof error === "object" && error !== null ? (error as RevenueCatErrorLike) : {};
  const errorCode = asFiniteNumber(candidate.errorCode);
  const category = categoryForErrorCode(errorCode);

  return {
    category,
    errorCode,
    statusCode: asFiniteNumber(candidate.extra?.statusCode),
    backendErrorCode: asSafeBackendCode(candidate.extra?.backendErrorCode),
    isCancelled: category === "user_cancelled",
    requiresVerification:
      category === "network" ||
      category === "operation_in_progress" ||
      category === "payment_pending" ||
      category === "store_problem" ||
      category === "already_purchased" ||
      category === "unknown",
  };
}
